import { useEffect, useRef, useState } from 'react';
import { api, type Schema } from '../api/client';
import { apiError } from '../api/session';
import { Button, Empty, ErrorState, Field, FilePicker, Table, Tabs } from '../ui';
import {
  AdminConfirm,
  AdminForm,
  AdminLink,
  AdminSelect,
  InfoRows,
  QueryState,
  dateLabel,
  dateTimeLabel,
  kindLabels,
  moneyLabel,
  useAdminAction,
  useAdminQuery,
  useAdministrative,
  type Me,
} from './common';

type Settings = Schema<'AdminIntegrationSettings'>;
type Job = Schema<'AdminIntegrationJob'>;
type Preview = Schema<'AdminImportPreview'>;
type BackupMetadata = Schema<'AdminSettings'>['backup'];
const integrationSettingsPath = '/api/v1/administrative/integrations/settings' as const;
const terminalStatuses = new Set(['COMPLETED', 'FAILED']);
const jobLabels: Record<string, string> = {
  IMPORT: 'Импорт записей',
  PROXY_CHECK: 'Проверка прокси',
  MAINTENANCE: 'Обслуживание базы',
};
const statusLabels: Record<string, string> = {
  PENDING: 'В очереди',
  RUNNING: 'Выполняется',
  COMPLETED: 'Завершено',
  FAILED: 'Не выполнено',
  AVAILABLE: 'Работает',
  UNAVAILABLE: 'Недоступен',
  SKIPPED: 'Пропущено',
};
const errorLabels: Record<string, string> = {
  IMPORT_DUPLICATE: 'Повторная запись. Проверьте существующие ресурсы и строки файла.',
  VALIDATION_FAILED: 'Проверьте формат строки и обязательные поля.',
  PERMISSION_DENIED: 'Нет доступа к этой записи или действие больше не разрешено.',
  RESOURCE_NOT_FOUND: 'Ресурс, лист или значение справочника недоступны.',
  VERSION_CONFLICT: 'Запись изменена. Обновите данные и повторите действие.',
  INVALID_STATE_TRANSITION: 'Состояние записи изменилось или предпросмотр истёк.',
  IDEMPOTENCY_KEY_REUSED: 'Операция с этим ключом уже была создана для других данных.',
  SERVICE_UNAVAILABLE: 'Операцию не удалось выполнить. Повторите позже.',
  PROXY_ADDRESS_BLOCKED: 'Проверка этого адреса запрещена.',
  PROXY_INVALID_HOST: 'Проверьте адрес прокси.',
  PROXY_INVALID_PORT: 'Проверьте порт прокси.',
  PROXY_AUTH_REJECTED: 'Прокси отклонил авторизацию.',
  PROXY_TIMEOUT: 'Прокси не ответил вовремя.',
  PROXY_CONNECTION_FAILED: 'Соединение с прокси не установлено.',
};
const errorLabel = (code?: string) =>
  code ? errorLabels[code] || 'Не удалось выполнить действие для этой записи.' : 'Без ошибки';
const asError = (error: unknown, message: string) =>
  error instanceof Error ? error : new Error(message);

export function AdministrativeSettings() {
  const me = useAdministrative();
  const canRead = me.allowedActions.includes('settings.read');
  const canEdit = me.allowedActions.includes('settings.edit');
  const settings = useAdminQuery<Schema<'AdminSettings'>>(
    '/api/v1/administrative/settings',
    {},
    {},
    canRead,
  );
  const integrations = useAdminQuery<Settings>(integrationSettingsPath, {}, {}, canEdit);
  if (!canRead) return <Empty title="Настройки базы недоступны" />;
  return (
    <>
      <nav className="admin-toolbar" aria-label="Настройки базы">
        <AdminLink className="pill" path="projects">
          Проекты
        </AdminLink>
        <AdminLink className="pill" path="dictionaries">
          Справочники
        </AdminLink>
        <AdminLink className="pill" path="telegram">
          Telegram
        </AdminLink>
      </nav>
      <QueryState query={settings} />
      {settings.data && (
        <section className="panel admin-integration-panel">
          <h2>Хранение и календарь</h2>
          <div className="admin-action-block">
            <InfoRows
              rows={[
                ['Часовой пояс', settings.data.timezone],
                ['Корзина', `${settings.data.trashRetentionDays} дней`],
                ['Журнал действий', `${settings.data.auditRetentionDays} дней`],
                ['Срок хранения резервных копий', `${settings.data.backupRetentionDays} дней`],
              ]}
            />
            <p className="muted">История фактических оплат сохраняется после удаления ресурса.</p>
            <BackupStatus backup={settings.data.backup} timezone={me.timezone} />
          </div>
        </section>
      )}
      {canEdit && <QueryState query={integrations} />}
      {canEdit && integrations.data && <IntegrationSettingsForm settings={integrations.data} />}
    </>
  );
}

function BackupStatus({ backup, timezone }: { backup: BackupMetadata; timezone: string }) {
  const label = !backup
    ? 'Статус временно недоступен'
    : !backup.configured
      ? 'Автоматические копии не настроены'
      : backup.lastResult === 'VERIFIED'
        ? 'Восстановление копии проверено'
        : backup.lastResult === 'FAILED'
          ? 'Последняя проверка не прошла'
          : backup.lastResult === 'NOT_RUN'
            ? 'Ожидает первой проверенной копии'
            : 'Статус временно недоступен';
  return (
    <div className="admin-action-block">
      <h2>Резервные копии</h2>
      <InfoRows
        rows={[
          ['Состояние', label],
          [
            'Последний запуск',
            backup?.lastAttemptAt ? dateTimeLabel(backup.lastAttemptAt, timezone) : 'Не выполнялся',
          ],
          [
            'Последний успешный запуск',
            backup?.lastSuccessAt
              ? dateTimeLabel(backup.lastSuccessAt, timezone)
              : 'Нет подтверждения',
          ],
          [
            'Проверка восстановления',
            backup?.lastRestoreVerifiedAt
              ? dateTimeLabel(backup.lastRestoreVerifiedAt, timezone)
              : 'Нет подтверждения',
          ],
          [
            'Размер пакета',
            backup?.bytes == null
              ? 'Не указан'
              : `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(backup.bytes / (1024 * 1024))} МБ`,
          ],
        ]}
      />
    </div>
  );
}

function settingsDraft(settings: Settings) {
  return {
    version: settings.version,
    remindersEnabled: settings.remindersEnabled,
    proxyScheduleEnabled: settings.proxyScheduleEnabled,
    minutes: String(settings.proxyScheduleMinutes),
    timeoutSeconds: String(settings.proxyTimeoutMs / 1000),
    concurrency: String(settings.proxyConcurrency),
    retries: String(settings.proxyRetries),
  };
}

function IntegrationSettingsForm({ settings }: { settings: Settings }) {
  const action = useAdminAction();
  const [draft, setDraft] = useState(() => settingsDraft(settings));
  const stale = settings.version !== draft.version;
  return (
    <AdminForm
      title="Напоминания и проверки прокси"
      headingLevel="h2"
      action={action}
      cancel="settings"
      submit={async () => {
        const result = await action.run<Settings>('PATCH', integrationSettingsPath, {
          version: draft.version,
          remindersEnabled: draft.remindersEnabled,
          proxyScheduleEnabled: draft.proxyScheduleEnabled,
          proxyScheduleMinutes: Number(draft.minutes),
          proxyTimeoutMs: Number(draft.timeoutSeconds) * 1000,
          proxyConcurrency: Number(draft.concurrency),
          proxyRetries: Number(draft.retries),
        });
        if (result) setDraft(settingsDraft(result));
      }}
    >
      {stale && (
        <div className="admin-action-block" role="status">
          <p>Другой сотрудник изменил настройки. Ваши введённые значения сохранены.</p>
          <Button onClick={() => setDraft(settingsDraft(settings))}>Загрузить новую версию</Button>
        </div>
      )}
      <label className="admin-check">
        <input
          type="checkbox"
          checked={draft.remindersEnabled}
          onChange={(event) =>
            setDraft((value) => ({ ...value, remindersEnabled: event.target.checked }))
          }
        />
        <span>Напоминать о сроках продления</span>
      </label>
      <p className="muted">
        За 7, 3 и 1 день, в день окончания и после него. Telegram подключается отдельно для каждого
        сотрудника.
      </p>
      <label className="admin-check">
        <input
          type="checkbox"
          checked={draft.proxyScheduleEnabled}
          onChange={(event) =>
            setDraft((value) => ({ ...value, proxyScheduleEnabled: event.target.checked }))
          }
        />
        <span>Автоматически проверять текущие прокси</span>
      </label>
      <div className="admin-filters">
        <Field
          label="Интервал проверки, минут"
          type="number"
          min={5}
          max={10080}
          step={1}
          required
          value={draft.minutes}
          onChange={(event) => setDraft((value) => ({ ...value, minutes: event.target.value }))}
        />
        <Field
          label="Ожидание ответа, секунд"
          type="number"
          min={1}
          max={30}
          step={1}
          required
          value={draft.timeoutSeconds}
          onChange={(event) =>
            setDraft((value) => ({ ...value, timeoutSeconds: event.target.value }))
          }
        />
        <Field
          label="Одновременно проверять"
          type="number"
          min={1}
          max={8}
          step={1}
          required
          value={draft.concurrency}
          onChange={(event) => setDraft((value) => ({ ...value, concurrency: event.target.value }))}
        />
        <Field
          label="Повторов при ошибке"
          type="number"
          min={0}
          max={2}
          step={1}
          required
          value={draft.retries}
          onChange={(event) => setDraft((value) => ({ ...value, retries: event.target.value }))}
        />
      </div>
    </AdminForm>
  );
}

export function AdministrativeTelegram() {
  const me = useAdministrative();
  const canConfigure = me.allowedActions.includes('settings.edit');
  const settings = useAdminQuery<Settings>(integrationSettingsPath, {}, {}, canConfigure);
  const binding = useAdminQuery<Schema<'AdminTelegramBinding'>>(
    '/api/v1/administrative/integrations/telegram/binding',
  );
  const action = useAdminAction();
  const [enrollment, setEnrollment] = useState<Schema<'AdminTelegramEnrollment'> | null>(null);
  const [linkPending, setLinkPending] = useState(false);
  const [linkError, setLinkError] = useState<Error | null>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => {
    const clear = () => {
      request.current?.abort();
      setEnrollment(null);
      setLinkPending(false);
    };
    window.addEventListener('blur', clear);
    return () => {
      request.current?.abort();
      window.removeEventListener('blur', clear);
    };
  }, []);
  useEffect(() => {
    if (!enrollment) return;
    const clear = () => setEnrollment(null);
    const timer = setTimeout(clear, Math.max(0, Date.parse(enrollment.expiresAt) - Date.now()));
    return () => clearTimeout(timer);
  }, [enrollment]);
  const createLink = async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLinkPending(true);
    setLinkError(null);
    setEnrollment(null);
    try {
      // One-time links stay in local component state and never enter the shared query cache.
      const result = await api.GET('/api/v1/administrative/integrations/telegram/enrollment', {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (result.error || !result.data || !result.response.ok)
        throw apiError(result.response, result.error);
      const url = new URL(result.data.url);
      if (
        url.protocol !== 'https:' ||
        url.hostname !== 't.me' ||
        !/^[A-Za-z0-9_-]{43}$/.test(url.searchParams.get('start') || '') ||
        Date.parse(result.data.expiresAt) <= Date.now()
      )
        throw new Error('Ссылка Telegram недоступна. Создайте её повторно.');
      if (!controller.signal.aborted) setEnrollment(result.data);
    } catch (error) {
      if (!controller.signal.aborted)
        setLinkError(asError(error, 'Не удалось создать ссылку Telegram'));
    } finally {
      if (!controller.signal.aborted) setLinkPending(false);
    }
  };
  return (
    <>
      <nav className="admin-toolbar" aria-label="Уведомления Telegram">
        <AdminLink className="pill" path="settings">
          Настройки базы
        </AdminLink>
      </nav>
      <section className="panel admin-integration-panel">
        <h2>Ваш личный Telegram</h2>
        <div className="admin-action-block">
          <QueryState query={binding} />
          {binding.data && (
            <>
              <p>
                {binding.data.linked ? 'Личный чат подключён.' : 'Личный чат ещё не подключён.'}
              </p>
              <Button disabled={linkPending} onClick={() => void createLink()}>
                {linkPending
                  ? 'Создание ссылки…'
                  : binding.data.linked
                    ? 'Привязать другой личный чат'
                    : 'Привязать личный Telegram'}
              </Button>
              {binding.data.linked && (
                <label className="admin-check">
                  <input
                    type="checkbox"
                    checked={binding.data.optedIn}
                    disabled={action.pending}
                    onChange={(event) =>
                      void action.run(
                        'PATCH',
                        '/api/v1/administrative/integrations/telegram/binding',
                        { optedIn: event.target.checked },
                        {},
                        'Настройка уведомлений сохранена',
                      )
                    }
                  />
                  <span>Получать напоминания в Telegram</span>
                </label>
              )}
            </>
          )}
          {enrollment && (
            <div className="admin-action-block" role="status">
              <p>
                Откройте личный чат и нажмите «Запустить». Ссылка действует до{' '}
                {dateTimeLabel(enrollment.expiresAt, me.timezone)}.
              </p>
              <a
                className="pill primary"
                href={enrollment.url}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
              >
                Открыть Telegram
              </a>
            </div>
          )}
          {linkError && <ErrorState error={linkError} />}
          {action.error && <ErrorState error={action.error} />}
          <p className="muted">
            Напоминания приходят в личный чат по ресурсам, доступным вам в базе.
          </p>
        </div>
      </section>
      {canConfigure && <QueryState query={settings} />}
      {canConfigure && settings.data && <TelegramConfiguration settings={settings.data} />}
    </>
  );
}

function TelegramConfiguration({ settings }: { settings: Settings }) {
  const action = useAdminAction();
  const connection = useAdminAction();
  const [token, setToken] = useState('');
  return (
    <>
      <section className="panel admin-integration-panel">
        <h2>Бот платформы</h2>
        <div className="admin-action-block">
          <InfoRows
            rows={[
              ['Бот', settings.telegramUsername ? `@${settings.telegramUsername}` : 'Не настроен'],
              [
                'Подключение',
                settings.webhookStatus === 'CONNECTED'
                  ? 'Подключён'
                  : settings.webhookStatus === 'CONFLICT'
                    ? 'Используется другим приложением'
                    : settings.configured
                      ? 'Ожидает подключения'
                      : 'Не настроено',
              ],
            ]}
          />
          {settings.webhookStatus === 'CONFLICT' ? (
            <p className="admin-overdue">
              У этого бота уже настроено другое приложение. Переключение нужно согласовать с
              администратором или подключить отдельного бота.
            </p>
          ) : settings.configured ? (
            <Button
              disabled={connection.pending}
              onClick={() =>
                void connection.run(
                  'POST',
                  '/api/v1/administrative/integrations/telegram/connect',
                  {},
                  {},
                  'Бот подключён',
                )
              }
            >
              {connection.pending
                ? 'Подключение…'
                : settings.webhookStatus === 'CONNECTED'
                  ? 'Обновить подключение'
                  : 'Подключить бота'}
            </Button>
          ) : null}
          {connection.error && <ErrorState error={connection.error} />}
        </div>
      </section>
      <AdminForm
        title={settings.configured ? 'Изменить бота' : 'Настроить бота'}
        headingLevel="h2"
        action={action}
        cancel="telegram"
        label="Сохранить бота"
        submit={async () => {
          if (
            await action.run<Settings>(
              'POST',
              '/api/v1/administrative/integrations/telegram/config',
              { token: token.trim() },
              {},
              'Бот сохранён',
            )
          )
            setToken('');
        }}
      >
        <Field
          label="Токен бота"
          type="password"
          autoComplete="new-password"
          required
          maxLength={300}
          disabled={action.pending}
          value={token}
          onChange={(event) => setToken(event.target.value)}
        />
        <p className="muted">
          Токен хранится зашифрованным. После сохранения подключите бота к платформе.
        </p>
      </AdminForm>
    </>
  );
}

const csvCell = (value: string) => `"${value.replaceAll('"', '""')}"`;
const csvText = (rows: string[][]) => rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
function readCsv(source: string): string[][] {
  const text = source.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let headerQuoted = false,
    commas = 0,
    semicolons = 0;
  for (let index = 0; index < text.length; index++) {
    const character = text[index];
    if (character === '"') {
      if (headerQuoted && text[index + 1] === '"') index++;
      else headerQuoted = !headerQuoted;
    } else if (!headerQuoted) {
      if (character === '\r' || character === '\n') break;
      if (character === ',') commas++;
      if (character === ';') semicolons++;
    }
  }
  const delimiter = commas >= semicolons ? ',' : ';';
  let row: string[] = [],
    cell = '',
    quoted = false,
    closedQuote = false;
  for (let index = 0; index < text.length; index++) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') {
        cell += '"';
        index++;
      } else if (character === '"') {
        quoted = false;
        closedQuote = true;
      } else cell += character;
    } else if (character === '"') {
      if (cell || closedQuote) throw new Error('В CSV некорректные кавычки. Проверьте файл.');
      quoted = true;
    } else if (character === delimiter) {
      row.push(cell);
      cell = '';
      closedQuote = false;
    } else if (character === '\r' || character === '\n') {
      if (character === '\r' && text[index + 1] === '\n') index++;
      row.push(cell);
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = '';
      closedQuote = false;
    } else {
      if (closedQuote) throw new Error('В CSV некорректная ячейка после кавычек. Проверьте файл.');
      cell += character;
    }
  }
  if (quoted) throw new Error('В CSV не закрыты кавычки. Проверьте файл.');
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
}

function prepareCsv(source: string, me: Me, defaultServiceType: string): string {
  const rows = readCsv(source);
  const header = rows[0];
  if (!header) throw new Error('Файл пуст. Выберите CSV с заголовком и записями.');
  const references: Record<string, { column: string; options: { id: string; name: string }[] }> = {
    проект: { column: 'projectId', options: me.projects },
    projectid: { column: 'projectId', options: me.projects },
    провайдер: { column: 'providerAccountId', options: me.providers },
    'учётная запись': { column: 'providerAccountId', options: me.providers },
    provideraccountid: { column: 'providerAccountId', options: me.providers },
    ...Object.fromEntries(
      [
        ['период оплаты', 'paymentPeriodId', 'PERIOD'],
        ['paymentperiodid', 'paymentPeriodId', 'PERIOD'],
        ['способ оплаты', 'paymentMethodId', 'METHOD'],
        ['paymentmethodid', 'paymentMethodId', 'METHOD'],
        ['статус', 'statusId', 'STATUS'],
        ['statusid', 'statusId', 'STATUS'],
        ['актуальность', 'relevanceId', 'RELEVANCE'],
        ['relevanceid', 'relevanceId', 'RELEVANCE'],
        ['тип услуги', 'serviceTypeId', 'SERVICE_TYPE'],
        ['servicetypeid', 'serviceTypeId', 'SERVICE_TYPE'],
      ].map(([name, column, kind]) => [
        name,
        { column, options: me.dictionaries.filter((item) => item.kind === kind) },
      ]),
    ),
  };
  header.forEach((label, columnIndex) => {
    const reference = references[label.trim().toLowerCase()];
    if (!reference) return;
    header[columnIndex] = reference.column;
    rows.slice(1).forEach((values) => {
      const value = values[columnIndex]?.trim();
      if (!value) return;
      const matches = reference.options.filter(
        (option) => option.id === value || option.name.toLowerCase() === value.toLowerCase(),
      );
      if (matches.length === 1) values[columnIndex] = matches[0]!.id;
    });
  });
  if (defaultServiceType && !header.some((label) => label === 'serviceTypeId')) {
    header.push('serviceTypeId');
    rows.slice(1).forEach((row) => row.push(defaultServiceType));
  } else if (defaultServiceType) {
    const index = header.indexOf('serviceTypeId');
    rows.slice(1).forEach((row) => {
      if (!row[index]?.trim()) row[index] = defaultServiceType;
    });
  }
  return csvText(rows);
}

function previewAddress(row: Preview['rows'][number]['data'], me: Me) {
  const value = row.metadata;
  return value.host
    ? `${value.protocol || 'HTTP'} · ${value.host}:${value.port}`
    : value.domain ||
        value.ip ||
        me.dictionaries.find((item) => item.id === value.serviceTypeId)?.name ||
        'Не указано';
}

export function AdministrativeImport({ sheetId }: { sheetId?: string }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const [selected, setSelected] = useState(sheetId || '');
  const sheet = me.sheets.find((value) => value.id === selected);
  const [mode, setMode] = useState('csv');
  const [source, setSource] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [expired, setExpired] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [completed, setCompleted] = useState<Job['result']>(null);
  const [filePending, setFilePending] = useState(false);
  const fileGeneration = useRef(0);
  const canImport =
    !!sheet && (me.allowedActions.includes('import') || sheet.allowedActions.includes('import'));
  const canExport =
    !!sheet && (me.allowedActions.includes('export') || sheet.allowedActions.includes('export'));
  useEffect(
    () => () => {
      fileGeneration.current++;
    },
    [],
  );
  useEffect(() => {
    if (!preview) return;
    const timer = setTimeout(
      () => setExpired(true),
      Math.max(0, Date.parse(preview.expiresAt) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [preview]);
  const updateSource = (value: string) => {
    setSource(value);
    setPreview(null);
    action.setError(null);
  };
  return (
    <>
      <nav className="admin-toolbar" aria-label="Импорт и экспорт">
        <AdminLink className="pill" path="resources">
          Ресурсы
        </AdminLink>
      </nav>
      <section className="panel admin-integration-panel">
        <div className="admin-action-block">
          <AdminSelect
            label="Лист"
            options={me.sheets.map((sheet) => ({
              id: sheet.id,
              name: `${me.sections.find((section) => section.id === sheet.sectionId)?.name || 'Раздел'} · ${sheet.name}`,
            }))}
            value={selected}
            onChange={(value) => {
              fileGeneration.current++;
              setFilePending(false);
              setMode('csv');
              setSelected(value);
              setPreview(null);
              setJobId(null);
              setCompleted(null);
              setServiceType('');
            }}
            required
          />
          {sheet && (
            <p className="muted">
              {kindLabels[sheet.kind]} ·{' '}
              {me.sections.find((section) => section.id === sheet.sectionId)?.name}
            </p>
          )}
        </div>
      </section>
      {canImport ? (
        <section className="panel admin-integration-panel">
          <h2>Добавить записи</h2>
          <div className="admin-action-block">
            <Tabs
              value={mode}
              onChange={(value) => {
                fileGeneration.current++;
                setFilePending(false);
                setMode(value);
                updateSource('');
              }}
              tabs={[
                { value: 'csv', label: 'CSV-файл' },
                ...(sheet.kind === 'PROXY' ? [{ value: 'proxies', label: 'Строки прокси' }] : []),
              ]}
            />
            {mode === 'csv' && (
              <>
                <FilePicker
                  label="Выбрать CSV-файл"
                  description="UTF-8, до 2 МБ и 1000 записей. Первая строка содержит названия столбцов."
                  accept=".csv,text/csv"
                  disabled={action.pending || filePending}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2_000_000) {
                      action.setError(
                        new Error('Файл превышает 2 МБ. Разделите его на несколько частей.'),
                      );
                      return;
                    }
                    const generation = ++fileGeneration.current;
                    setFilePending(true);
                    void file
                      .text()
                      .then((value) => {
                        if (generation === fileGeneration.current) updateSource(value);
                      })
                      .catch(() =>
                        action.setError(
                          new Error('Не удалось прочитать файл. Выберите его повторно.'),
                        ),
                      )
                      .finally(() => {
                        if (generation === fileGeneration.current) setFilePending(false);
                      });
                  }}
                />
                <p className="muted">
                  Поддерживаются название, проект, провайдер, период оплаты, стоимость, валюта,
                  «Оплачен до» и комментарий. Справочники указывайте по названию.
                </p>
              </>
            )}
            {sheet.kind === 'SERVICE' && (
              <AdminSelect
                label="Тип услуги для строк без типа"
                options={me.dictionaries.filter((item) => item.kind === 'SERVICE_TYPE')}
                value={serviceType}
                onChange={(value) => {
                  setServiceType(value);
                  setPreview(null);
                }}
              />
            )}
            <Field label={mode === 'proxies' ? 'Прокси, по одному в строке' : 'Содержимое CSV'}>
              <textarea
                rows={8}
                value={source}
                maxLength={2_000_000}
                spellCheck={false}
                disabled={action.pending || filePending}
                placeholder={
                  mode === 'proxies'
                    ? 'host:port\nprotocol://login:password@host:port'
                    : sheet.kind === 'PROXY'
                      ? 'Название,Прокси\nОсновной,host:port'
                      : sheet.kind === 'SERVER'
                        ? 'Название,IP\nОсновной,203.0.113.10'
                        : sheet.kind === 'DOMAIN'
                          ? 'Название,Домен\nОсновной,example.com'
                          : 'Название,Тип услуги\nОсновной,DNS'
                }
                onChange={(event) => updateSource(event.target.value)}
              />
            </Field>
            {action.error && !confirm && <ErrorState error={action.error} />}
            <Button
              className="primary"
              disabled={!source.trim() || action.pending || filePending}
              onClick={async () => {
                try {
                  const csv =
                    mode === 'proxies'
                      ? csvText([
                          ['Прокси'],
                          ...source
                            .split(/\r?\n/)
                            .map((value) => value.trim())
                            .filter(Boolean)
                            .map((value) => [value]),
                        ])
                      : prepareCsv(source, me, sheet.kind === 'SERVICE' ? serviceType : '');
                  if (new TextEncoder().encode(csv).length > 2_000_000)
                    throw new Error('Данные превышают 2 МБ. Разделите их на несколько частей.');
                  const result = await action.run<Preview>(
                    'POST',
                    '/api/v1/administrative/integrations/imports/preview',
                    { sheetId: selected, kind: sheet.kind, csv },
                    {},
                    '',
                  );
                  if (result) {
                    setPreview(result);
                    setExpired(false);
                    setJobId(null);
                    setCompleted(null);
                  }
                } catch (error) {
                  action.setError(asError(error, 'Проверьте формат CSV'));
                }
              }}
            >
              {filePending
                ? 'Чтение файла…'
                : action.pending
                  ? 'Проверка…'
                  : 'Проверить и показать предпросмотр'}
            </Button>
          </div>
        </section>
      ) : selected ? (
        <Empty
          title="Импорт в этот лист недоступен"
          subtitle="Проверьте разрешения или выберите другой лист."
        />
      ) : null}
      {preview && (
        <section className="panel admin-integration-panel">
          <header className="admin-heading">
            <h2>Предпросмотр импорта</h2>
            <span>
              {preview.validCount} готово · {preview.errors.length} ошибок
            </span>
          </header>
          <div className="admin-action-block">
            <p className="muted">
              Данные подключения скрыты.{' '}
              {expired
                ? 'Предпросмотр истёк. Проверьте данные ещё раз.'
                : `Можно сохранить до ${dateTimeLabel(preview.expiresAt, me.timezone)}.`}
            </p>
            <Table
              rows={preview.rows.map((row) => ({
                ...row,
                id: String(row.row),
                name: row.data.name,
              }))}
              columns={[
                { key: 'row', label: 'Строка' },
                { key: 'name', label: 'Название', render: (row) => row.data.name },
                {
                  key: 'address',
                  label: 'Ресурс',
                  sortable: false,
                  filter: false,
                  render: (row) => previewAddress(row.data, me),
                },
                {
                  key: 'project',
                  label: 'Проект',
                  sortable: false,
                  filter: false,
                  render: (row) =>
                    me.projects.find((project) => project.id === row.data.projectId)?.name ||
                    'Не указан',
                },
                {
                  key: 'cost',
                  label: 'Стоимость',
                  sortable: false,
                  filter: false,
                  render: (row) =>
                    row.data.priceKind === 'FREE'
                      ? 'Бесплатно'
                      : moneyLabel(row.data.cost, row.data.currency),
                },
                {
                  key: 'paidUntil',
                  label: 'Оплачен до',
                  sortable: false,
                  filter: false,
                  render: (row) => dateLabel(row.data.paidUntil),
                },
                {
                  key: 'credentials',
                  label: 'Подключение',
                  sortable: false,
                  filter: false,
                  render: (row) => (row.data.credentials ? '••••••' : 'Без пароля'),
                },
              ]}
              empty="Нет строк, готовых к импорту"
            />
            {preview.errors.length > 0 && (
              <Table
                rows={preview.errors.map((row) => ({ ...row, id: String(row.row) }))}
                columns={[
                  { key: 'row', label: 'Строка с ошибкой' },
                  { key: 'error', label: 'Причина', render: (row) => errorLabel(row.code) },
                ]}
              />
            )}
            <Button
              className="primary"
              disabled={expired || !preview.validCount || action.pending}
              onClick={() => setConfirm(true)}
            >
              Импортировать {preview.validCount} записей
            </Button>
          </div>
        </section>
      )}
      {confirm && preview && (
        <AdminConfirm
          title="Импортировать записи?"
          action={action}
          label="Импортировать"
          onClose={() => setConfirm(false)}
          onConfirm={async () => {
            const result = await action.run<Schema<'AdminImportCommit'>>(
              'POST',
              '/api/v1/administrative/integrations/imports/{id}/commit',
              {},
              { id: preview.id },
              'Импорт принят',
            );
            if (result) {
              setConfirm(false);
              setSource('');
              setPreview(null);
              if ('id' in result) setJobId(result.id);
              else setCompleted(result.result);
            }
          }}
        >
          <p>
            В лист «{sheet?.name}» будут добавлены {preview.validCount} записей.
          </p>
          {preview.errors.length > 0 && (
            <p>
              Строки с ошибками ({preview.errors.length}) пропускаются. Для их добавления исправьте
              данные и создайте новый предпросмотр.
            </p>
          )}
        </AdminConfirm>
      )}
      {jobId && <AdministrativeJob id={jobId} embedded />}
      {completed && (
        <section className="panel admin-integration-panel">
          <h2>Импорт завершён</h2>
          <div className="admin-action-block">
            <JobResult result={completed} />
          </div>
        </section>
      )}
      {canExport && (
        <ExportPanel
          key={selected}
          sheetId={selected}
          allowCredentials={
            me.capabilities.connectionRead || !!sheet?.allowedActions.includes('connection.read')
          }
        />
      )}
    </>
  );
}

function ExportPanel({
  sheetId,
  allowCredentials,
}: {
  sheetId: string;
  allowCredentials: boolean;
}) {
  const [credentials, setCredentials] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  const download = async () => {
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setError(null);
    try {
      const result = await api.GET('/api/v1/administrative/integrations/export.csv', {
        params: {
          query: {
            sheetId,
            includeCredentials: credentials && allowCredentials ? 'true' : 'false',
          },
        },
        parseAs: 'blob',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (result.error || !result.data || !result.response.ok)
        throw apiError(result.response, result.error);
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(result.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'it-resources.csv';
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      if (!controller.signal.aborted) setError(asError(error, 'Не удалось скачать CSV'));
    } finally {
      if (!controller.signal.aborted) {
        setPending(false);
      }
    }
  };
  return (
    <section className="panel admin-integration-panel">
      <h2>Экспорт листа</h2>
      <div className="admin-action-block">
        <p className="muted">
          CSV содержит доступные вам записи и поля. Финансовые данные учитывают ваши разрешения.
        </p>
        {allowCredentials && (
          <label className="admin-check">
            <input
              type="checkbox"
              checked={credentials}
              disabled={pending}
              onChange={(event) => setCredentials(event.target.checked)}
            />
            <span>Включить данные подключения и пароли</span>
          </label>
        )}
        <Button disabled={pending} onClick={() => void download()}>
          {pending ? 'Подготовка файла…' : 'Скачать CSV'}
        </Button>
        {error && <ErrorState error={error} />}
      </div>
    </section>
  );
}

export function AdministrativeJob({ id, embedded = false }: { id: string; embedded?: boolean }) {
  const me = useAdministrative();
  const query = useAdminQuery<Job>('/api/v1/administrative/integrations/jobs/{id}', { id });
  const active =
    !!query.data &&
    !terminalStatuses.has(query.data.status) &&
    ['PENDING', 'RUNNING'].includes(query.data.status);
  useEffect(() => {
    if (!active || query.error) return;
    const timer = setInterval(() => {
      void query.refetch({ cancelRefetch: false });
    }, 2500);
    return () => clearInterval(timer);
  }, [active, query.error, query.refetch]);
  if (!query.data)
    return (
      <>
        {!embedded && <h1>Операция с ресурсами</h1>}
        <QueryState query={query} />
      </>
    );
  const job = query.data;
  const Heading = embedded ? 'h2' : 'h1';
  return (
    <section className="panel admin-integration-panel" aria-live="polite">
      <header className="admin-heading">
        <Heading>{jobLabels[job.type] || 'Операция с ресурсами'}</Heading>
        <span>{statusLabels[job.status] || 'Состояние уточняется'}</span>
      </header>
      <div className="admin-action-block">
        <p className="muted">
          Создано {dateTimeLabel(job.createdAt, me.timezone)} · обновлено{' '}
          {dateTimeLabel(job.updatedAt, me.timezone)}
        </p>
        {active && <p>Результаты появятся здесь автоматически.</p>}
        <QueryState query={query} />
        <JobResult result={job.result} />
        {job.type === 'IMPORT' && terminalStatuses.has(job.status) && (
          <AdminLink className="pill" path="import">
            Новый импорт
          </AdminLink>
        )}
        {job.type === 'PROXY_CHECK' && (
          <AdminLink className="pill" path="resources?kind=PROXY">
            Открыть реестр прокси
          </AdminLink>
        )}
      </div>
    </section>
  );
}

function JobResult({ result }: { result: Job['result'] }) {
  if (!result) return null;
  if ('error' in result)
    return (
      <p className="admin-overdue" role="alert">
        {errorLabel(result.error)}
      </p>
    );
  if ('rows' in result)
    return (
      <>
        <p>
          Добавлено: {result.createdCount}. Не добавлено:{' '}
          {result.rows.filter((row) => row.error).length}.
        </p>
        <Table
          rows={result.rows.map((row) => ({ ...row, resourceId: row.id, id: String(row.row) }))}
          columns={[
            { key: 'row', label: 'Строка' },
            {
              key: 'status',
              label: 'Результат',
              render: (row) => (row.error ? 'Не добавлена' : 'Добавлена'),
            },
            {
              key: 'resource',
              label: 'Запись',
              render: (row) => {
                return row.resourceId ? (
                  <AdminLink path={`resources/${row.resourceId}`}>Открыть ресурс</AdminLink>
                ) : (
                  errorLabel(row.error)
                );
              },
            },
          ]}
        />
      </>
    );
  if ('items' in result)
    return (
      <>
        <p>
          Работают: {result.items.filter((row) => row.status === 'AVAILABLE').length}. Недоступны:{' '}
          {result.items.filter((row) => row.status === 'UNAVAILABLE').length}. Пропущено:{' '}
          {result.items.filter((row) => row.status === 'SKIPPED').length}.
        </p>
        <Table
          rows={result.items}
          columns={[
            {
              key: 'resource',
              label: 'Прокси',
              render: (row) => <AdminLink path={`resources/${row.id}`}>Открыть прокси</AdminLink>,
            },
            { key: 'status', label: 'Проверка', render: (row) => statusLabels[row.status] },
            {
              key: 'error',
              label: 'Причина',
              render: (row) =>
                row.error
                  ? errorLabel(row.error)
                  : row.status === 'UNAVAILABLE'
                    ? 'Подробности в карточке прокси'
                    : 'Без ошибки',
            },
          ]}
        />
      </>
    );
  return <p>Удалено из корзины по сроку хранения: {result.purgedCount}.</p>;
}
